import type { Service } from '../services/api/services';

// ── Tree building (flat list -> children map, cycle-safe) ─────────────────
// Moved from ServiceManagementPage.tsx (KAN-157 god-component split): a pure
// tree-shape helper over Service[], the natural home next to isDescendant/canDrop.
export interface TreeData {
  roots: Service[];
  childrenByParent: Map<number, Service[]>;
}

export function buildTree(services: Service[]): TreeData {
  const byId = new Map<number, Service>();
  services.forEach(s => byId.set(s.id, s));

  const childrenByParent = new Map<number, Service[]>();
  const roots: Service[] = [];

  services.forEach(s => {
    // A node is a root if it has no parent OR points to a missing parent.
    if (s.parent === null || s.parent === undefined || !byId.has(s.parent)) {
      roots.push(s);
      return;
    }
    const siblings = childrenByParent.get(s.parent) || [];
    siblings.push(s);
    childrenByParent.set(s.parent, siblings);
  });

  // Sort siblings and roots by order for stable rendering.
  const byOrder = (a: Service, b: Service) => a.order - b.order;
  roots.sort(byOrder);
  childrenByParent.forEach(list => list.sort(byOrder));

  return { roots, childrenByParent };
}

export function isDescendant(nodeId: number, targetId: number, services: Service[]): boolean {
  const visited = new Set<number>();
  const queue = services.filter(s => s.parent === nodeId).map(s => s.id);
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === targetId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    services.filter(s => s.parent === current).forEach(s => queue.push(s.id));
  }
  return false;
}

export function canDrop(draggedId: number, targetDroppableId: string, services: Service[]): boolean {
  if (targetDroppableId === 'root') {
    const rootAggregator = services.find(s => s.parent === null && s.is_aggregator);
    const draggedService = services.find(s => s.id === draggedId);
    // Block only when a nested (non-root) service tries to move to root while a root
    // aggregator already exists. Root-level services can be freely reordered.
    if (rootAggregator && draggedService?.parent !== null && rootAggregator.id !== draggedId) {
      return false;
    }
  }
  if (targetDroppableId.startsWith('children-')) {
    const targetId = Number(targetDroppableId.replace('children-', ''));
    if (isDescendant(draggedId, targetId, services)) {
      return false;
    }
  }
  return true;
}
